import CachedIcon from "@mui/icons-material/Cached";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import "react-multi-date-picker/styles/colors/green.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getDefaultMRTOptions } from "../../dedicatedDelivery/DefaultMrtOptions";
import { CustomTooltip } from "../../../components/common/tooltip/CustomToolTip";
import getAxiosError from "../../../app/api/axiosError";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { FdpInvoiceListUIResponse } from "../../../app/models/ApiResponse";
import {
  ClientData,
  RatecardFdpInvoiceResponse,
} from "../../../app/models/responseModels/FdpInvoiceResponses";
import { FdpInvoiceAgent } from "../../../app/api/fdpInvoices/FdpInvoiceAgent";
import {
  FDP_INVOICE_COLUMN_NAME,
  FDP_INVOICE_LABEL_NAME,
  FDP_INVOICE_STATUS,
} from "./FdpInvoiceConstants";
import { useSubmitModal } from "../../../components/common/modalPopup/useSubmitModal";
import FdpInvoiceEditModal from "./modals/FdpInvoiceEditModal";
import { Theme } from "@mui/material";
import FdpInvoicePreviewModal from "./modals/FdpInvoicePreviewModal";
import SubmitFdpInvoiceModal from "./modals/SubmitFdpInvoiceModal";
import { formatISODateString } from "../../../components/common/utils/DateUtils";

export type FdpInvoiceListProps = {
  FdpId: number;
  companyName: string;
};

const FdpInvoiceList = (props: FdpInvoiceListProps) => {
  // access all parameters passed
  const fdpId = props?.FdpId;

  const dispatch = useAppDispatch();

  // import base configruation for grid
  const defaultMRTOptions = getDefaultMRTOptions<RatecardFdpInvoiceResponse>();

  // state to manage pagination values from grid
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const isFdc = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdc
  );

  const isFdp = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdp
  );

  const [showInvoiceEditModal, setShowInvoiceEditModal] = useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentInvoice, setCurrentInvoice] = useState<
    RatecardFdpInvoiceResponse | undefined
  >(undefined);
  const [fdpInvoiceListData, setFdpInvoiceListData] =
    useState<FdpInvoiceListUIResponse>();
  const [clientData, setClientData] = useState<ClientData[]>([]);

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const getInvoiceStatusCellColour = (
    invoiceStatus: string,
    theme: Theme
  ): string => {
    if (invoiceStatus === FDP_INVOICE_STATUS.DRAFT)
      return theme.palette.error.main;
    else if (invoiceStatus === FDP_INVOICE_STATUS.PROTEST)
      return theme.palette.warning.light;
    else if (
      invoiceStatus === FDP_INVOICE_STATUS.NEW_INVOICE ||
      invoiceStatus === FDP_INVOICE_STATUS.PAID
    )
      return theme.palette.success.main;
    else return theme.palette.success.light;
  };

  // ***** Callbacks *****
  const getFdpInvoiceListData = useCallback(async () => {
    try {
      const response = await FdpInvoiceAgent.getFdpInvoiceList(fdpId, {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      });
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "fdpInvoiceList",
        });
      } else {
        // Filter out "generated" status invoices if isFdp is true
        const filteredContent = isFdp
          ? response.data.content.filter(
              (item) => item.currentStatus?.internalStatus !== "fin_in_review"
            )
          : response.data.content;

        // Create a modified response with filtered content
        const filteredResponse = {
          ...response,
          data: {
            ...response.data,
            content: filteredContent,
          },
        };

        setFdpInvoiceListData(filteredResponse);

        let duplicateClientData: ClientData[] = [];
        // Use filteredContent instead of response.data.content
        filteredContent.forEach((fdpInvoice) => {
          let clientsData: ClientData[] = fdpInvoice.stores.map((store) => {
            let client: ClientData = {
              clientId: store.clientId,
              clientName: store.clientName,
            };
            return client;
          });

          duplicateClientData = duplicateClientData.concat(clientsData);
        });

        let clientIdList: number[] = [];
        let uniqueClientData: ClientData[] = [];

        duplicateClientData.forEach((value) => {
          if (!clientIdList.includes(value.clientId)) {
            clientIdList.push(value.clientId);
            uniqueClientData.push(value);
          }
        });

        setClientData(uniqueClientData);
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpInvoiceListException",
      });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, pagination, isFdp]); // Added isFdp to dependency array

  const fetchFdpInvoiceFile = async (
    fdpInvoiceId: number,
    invoiceStatus: string
  ) => {
    try {
      setIsLoading(true);
      const response = await FdpInvoiceAgent.fetchFdpInvoiceFile(
        fdpInvoiceId,
        invoiceStatus
      );
      if (response.status !== 200) {
        toast.error("Document couldn't retrived.", {
          toastId: "FetchFdpInvoiceFileError",
        });
      }
      const fileName = response.headers["file-name"];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error("File isn't accessible.", {
        toastId: "FetchFdpInvoiceFileException",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // set page title
  useEffect(() => {
    document.title = "Fdp's - Invoices";
  }, []);

  // ***** UseEffects *****
  useEffect(() => {
    setIsLoading(true);
    let isFunctionRun = true;
    if (isFunctionRun) {
      getFdpInvoiceListData();
    }

    return () => {
      isFunctionRun = false;
    };
  }, [getFdpInvoiceListData]);

  // map columns to show on grid
  const columns = useMemo<MRT_ColumnDef<RatecardFdpInvoiceResponse>[]>(
    () => [
      {
        accessorKey: FDP_INVOICE_COLUMN_NAME.INVOICE_STATUS,
        header: FDP_INVOICE_LABEL_NAME.INVOICE_STATUS,
        size: 100,
        Cell: ({ row }) => {
          const theme = useTheme();
          // Get background color based on the invoice status
          const bgColor = getInvoiceStatusCellColour(
            row?.original?.invoiceStatus,
            theme
          );
          return (
            <Box
              sx={{
                color: bgColor !== "white" ? "white" : "black",
                backgroundColor: bgColor,
                padding: "10px",
                borderRadius: "8px",
              }}
            >
              {isFdc
                ? row?.original?.currentStatus?.fdcExternalStatus
                : isFdp
                ? row?.original?.currentStatus?.fdpExternalStatus
                : ""}
            </Box>
          );
        },
      },
      {
        accessorKey: FDP_INVOICE_COLUMN_NAME.SL_NO,
        header: FDP_INVOICE_LABEL_NAME.SL_NO,
        size: 50,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: FDP_INVOICE_COLUMN_NAME.INVOICE_ID,
        header: FDP_INVOICE_LABEL_NAME.INVOICE_ID,
        size: 100,
      },
      {
        accessorKey: FDP_INVOICE_COLUMN_NAME.NET_PAYMENT,
        header: FDP_INVOICE_LABEL_NAME.NET_PAYMENT,
      },
      {
        accessorKey: FDP_INVOICE_COLUMN_NAME.ASSOCIATED_STORES,
        header: FDP_INVOICE_LABEL_NAME.ASSOCIATED_STORES,
        size: 100,
        Cell: ({ row }) => {
          return (
            row?.original?.stores.map((store) => store?.storeId).join(", ") ||
            ""
          );
        },
      },
      {
        accessorKey: FDP_INVOICE_COLUMN_NAME.ASSOCIATED_CLIENTS,
        header: FDP_INVOICE_LABEL_NAME.ASSOCIATED_CLIENTS,
        size: 100,
        Cell: () => {
          const clientNames = clientData.map((store) => store.clientName);
          return clientNames.join(",") || "";
        },
      },
      {
        accessorKey: FDP_INVOICE_COLUMN_NAME.INVOICE_GENERATION_DATE,
        header: FDP_INVOICE_LABEL_NAME.INVOICE_GENERATION_DATE,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
      // {
      //   accessorKey: FDP_INVOICE_COLUMN_NAME.CREATED_ON,
      //   header: FDP_INVOICE_LABEL_NAME.CREATED_ON,
      //   size: 100,
      //   Cell: ({ cell, row }) =>
      //     row.original.createdOn
      //       ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
      //           hourCycle: "h12",
      //         })
      //       : "",
      // },
      {
        accessorKey: FDP_INVOICE_COLUMN_NAME.RECEIPT_FILE,
        header: FDP_INVOICE_LABEL_NAME.RECEIPT_FILE,
        size: 100,
        Cell: ({ cell, row }) => {
          const { receiptUploadedOn, invoiceStatus, receiptFile, id } =
            row?.original; // Destructure row.original for createdOn and status

          // Handle file download
          const handleDownload = () => {
            if (receiptFile) {
              // Step 1: Create a Blob from the Base64 string
              const byteCharacters = atob(receiptFile); // Decode the base64 string to binary data
              const byteArrays = [];
              for (
                let offset = 0;
                offset < byteCharacters.length;
                offset += 1024
              ) {
                const slice = byteCharacters.slice(
                  offset,
                  Math.min(offset + 1024, byteCharacters.length)
                );
                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) {
                  byteNumbers[i] = slice.charCodeAt(i);
                }
                byteArrays.push(new Uint8Array(byteNumbers));
              }
              const blob = new Blob(byteArrays, {
                type: "application/octet-stream",
              }); // Adjust MIME type if necessary
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = row?.original?.receiptName; // You can set a custom filename here
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url); // Clean up the URL object after the download
            }
          };
          const receiptUploaded = receiptUploadedOn ? true : false;

          // Handle file upload
          const handleFileChange = async (event: any) => {
            const file = event.target.files[0];
            const formData = new FormData();
            if (file) {
              formData.append("file", file);
            }
            try {
              const response = await FdpInvoiceAgent.saveFdpReceiptFile(
                id,
                formData
              );
              if (response.includes("Receipt Uploaded Successfully")) {
                toast.success("Receipt Uploaded Succesfully ", {
                  toastId: "receiputUploadSuccess",
                });
                getFdpInvoiceListData();
              }
            } catch (error) {
              const appError = getAxiosError(error);
              toast.error("Error uploading Receipt.", {
                toastId: "receiptUploadException",
              });
            }
          };

          // Check if createdOn exists, if so show the date
          if (receiptUploaded) {
            return (
              <IconButton
                title="Download"
                color={"primary"}
                onClick={handleDownload}
              >
                <CloudDownloadIcon />
              </IconButton>
            );
          }

          // If status is 'FDP_Approved', show the file uploader
          if (
            invoiceStatus === "fdp_accepted" &&
            receiptUploaded === false &&
            isFdc
          ) {
            return (
              <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
              >
                Upload Reciept
                <VisuallyHiddenInput
                  type="file"
                  onChange={handleFileChange}
                  multiple
                />
              </Button>
            );
          }

          // Default fallback if neither condition is met
          return <span>No receipt uploaded</span>;
        },
      },
      // {
      //   accessorKey: FDP_INVOICE_COLUMN_NAME.RECEIPT_UPDATED_BY,
      //   header: FDP_INVOICE_LABEL_NAME.RECEIPT_UPDATED_BY,
      //   size: 100,
      // },
      // {
      //   accessorKey: FDP_INVOICE_COLUMN_NAME.RECEIPT_UPLOADED_ON,
      //   header: FDP_INVOICE_LABEL_NAME.RECEIPT_UPLOADED_ON,
      //   size: 100,
      //   Cell: ({ cell, row }) =>
      //     row.original.receiptUploadedOn
      //       ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
      //           hourCycle: "h12",
      //         })
      //       : "",
      // },
      {
        accessorKey: FDP_INVOICE_COLUMN_NAME.UPDATED_ON,
        header: FDP_INVOICE_LABEL_NAME.UPDATED_ON,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
    ],
    [clientData]
  );

  // elements to be rendered on top of grid
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<RatecardFdpInvoiceResponse>
  ) => {
    return (
      <Box>
        <CustomTooltip arrow title="Refresh" onClick={getFdpInvoiceListData}>
          <IconButton type="button">
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
      </Box>
    );
  };

  // open invoice preview modal logic
  const [invoicePreviewModal, openInvoicePreviewModal] = useSubmitModal(
    (closeInvoicePreviewModal) => (
      <FdpInvoicePreviewModal
        isView={false}
        invoice={currentInvoice}
        closeInvoicePreviewModal={() => {
          closeInvoicePreviewModal();
          getFdpInvoiceListData();
        }}
        showInvoiceEditModal={() => {
          closeInvoicePreviewModal();
          setShowInvoiceEditModal(true);
        }}
      />
    )
  );

  // open edit invoice modal logic
  const [submitInvoiceModal, openSubmitInvoiceModal] = useSubmitModal(
    (closeSubmitInvoiceModal) => (
      <SubmitFdpInvoiceModal
        invoice={currentInvoice}
        closeSubmitInvoiceModal={() => {
          closeSubmitInvoiceModal();
          // refresh client invoice list after edit
          setIsLoading(true);
          getFdpInvoiceListData();
        }}
      />
    )
  );

  // store grid configuration
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: fdpInvoiceListData?.data.content ?? [],
    manualPagination: true,
    onPaginationChange: setPagination,
    enableRowSelection: false,
    rowCount: fdpInvoiceListData?.data.totalElements,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {},
    },
    state: {
      pagination,
      showProgressBars: isLoading,
    },
    getRowId: (row) => `${row.id}_${row.invoiceStatus}`,
    enableRowActions: true,

    renderRowActions: ({ row }) => (
      <Box>
        <IconButton
          title="Edit / View"
          color="info"
          onClick={(e) => {
            setCurrentInvoice(row.original);
            openInvoicePreviewModal();
          }}
        >
          <VisibilityIcon />
        </IconButton>
        <IconButton
          title="Download"
          disabled={
            !row.original.invoiceFilePath || row.original.invoiceFilePath === ""
          }
          color={"primary"}
          onClick={() =>
            fetchFdpInvoiceFile(row.original.id, row?.original?.invoiceStatus)
          }
        >
          <CloudDownloadIcon />
        </IconButton>
      </Box>
    ),
    muiTableBodyRowProps: () => ({
      ...defaultMRTOptions.muiTableBodyRowProps,
    }),
    renderTopToolbarCustomActions: ({ table }) =>
      renderTopToolbarCustomActions(table),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {fdpInvoiceListData?.data.totalElements}
        </Typography>
      );
    },
  });

  return (
    <>
      <Grid
        container
        paddingY={2}
        rowSpacing={2}
        justifyContent="space-between"
      >
        <Grid item>
          <Typography component="h2" variant="h3">
            FDP: {props?.companyName}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12} marginTop={2}>
        <MaterialReactTable table={table} />
      </Grid>
      {invoicePreviewModal}
      {submitInvoiceModal}
      {showInvoiceEditModal ? (
        <FdpInvoiceEditModal
          fdpId={fdpId}
          fdpInvoice={currentInvoice}
          closeInvoiceEditModal={() => {
            setShowInvoiceEditModal(false);

            // refresh client invoice list after edit
            setIsLoading(true);
            getFdpInvoiceListData();
          }}
          onAddMoreStores={() => {
            setShowInvoiceEditModal(false);

            setTimeout(function () {
              // delay for 0.5 seconds so that the invoice is closed and it can be re-opened without any issues and with a state that is reset
              setShowInvoiceEditModal(true);
            }, 500);
          }}
          clients={clientData}
        />
      ) : null}
    </>
  );
};
export default FdpInvoiceList;
